import React, {useMemo} from 'react';
import {Link, useLocation} from 'react-router-dom';
import {Pagination, PaginationItem, PaginationLink} from 'reactstrap';
import {StandardPaginationProps} from '@src/@types/components/Pagination';
import {range} from '@src/helpers/range';
import useIsMobileViewport from '@src/hooks/useIsMobileViewport';

const voidFunction = () => void 0;

function AvoidStateMutation({...props}: Record<any, any>) {
    delete props.state;
    delete props.to;

    return <button type="button" {...props}/>;
}

export function StandardPagination(props: StandardPaginationProps & { isIsolatedComponent?: boolean }) {
    const firstPage = props.firstPage ?? 1;
    const currentPage = props.currentPage;
    const lastPage = props.lastPage;

    const isMobile = useIsMobileViewport();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);

    const onClick = props.onClick ?? voidFunction;

    const itemsNumber = isMobile ? 0 : Math.max(0, Math.min(2, props.itemsNumber ?? 2));

    const items = useMemo<number[]>(() => {
        const startPage = Math.max(2, currentPage - Math.max(1, itemsNumber));
        const endPage = Math.min(lastPage - 1, currentPage + Math.max(1, itemsNumber));
        let pages = range(startPage, endPage);

        const totalItems = (itemsNumber * 2) + 3;// 3 = first page + current page + last page controls
        if (totalItems > lastPage) {
            return range(firstPage, lastPage);
        }

        const hasPrevButton = startPage > 2;
        const hasNextButton = (lastPage - endPage) > 1;
        const offset = totalItems - (pages.length + 1);

        if (hasPrevButton && !hasNextButton) {
            // handle: (1) « {5 6} [7] {8 9} (10)
            pages = [
                -Infinity,
                ...(offset > 0 ? range(startPage - offset, startPage - 1) : []),
                ...pages
            ];

        } else if (!hasPrevButton && hasNextButton) {
            // handle: (1) {2 3} [4] {5 6} » (10)
            pages = [
                ...pages,
                ...(offset > 0 ? range(endPage + 1, endPage + offset) : []),
                Infinity
            ];

        } else if (hasPrevButton && hasNextButton) {
            // handle: (1) « {4 5} [6] {7 8} » (10)
            pages = [-Infinity, ...pages, Infinity];
        }

        pages = [...new Set(pages)];// array unique

        return [firstPage, ...pages, lastPage];
    }, [firstPage, currentPage, itemsNumber, lastPage]);

    return <Pagination {...(props.paginationProps ?? {})}>
        {items.map((pageNumber, index) => {
            const isPrev = pageNumber === -Infinity;
            const isNext = pageNumber === Infinity;
            const isCurrentPage = pageNumber === currentPage;

            const actualPageNumber = isPrev ? currentPage - 1 : (
                    isNext ? currentPage + 1 : pageNumber
            );

            searchParams.set(props.pageNumberGetParamName, String(actualPageNumber));

            return <PaginationItem
                    key={`${props.pageNumberGetParamName}-${index}-pagination-item-${pageNumber}`}
                    active={isCurrentPage}
                    disabled={isCurrentPage}
            >
                <PaginationLink
                        first={index === 0}
                        last={index === (items.length - 1)}
                        previous={isPrev}
                        next={isNext}
                        onClick={() => onClick(actualPageNumber)}
                        tag={props.isIsolatedComponent ? AvoidStateMutation : Link}
                        state={{from: currentPage}}
                        to={{...location, search: searchParams.toString()}}
                >
                    {isPrev ? '«' : isNext ? '»' : pageNumber}
                </PaginationLink>
            </PaginationItem>;
        })}
    </Pagination>;
}

export default React.memo(StandardPagination);
